import React, { useState, useEffect } from "react";
import axios from "axios";
import Header from "../Components/Header";
import SimpleTable from "../Components/SimpleTable";
import { BRAND } from "../css/brand";
import Badge from "../Components/Badge";
import Swal from "sweetalert2";
import { Download, RefreshCw } from "lucide-react";
import BlueButton from "../Components/BlueButton";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const rowperpage = 10;

  const authUserString = sessionStorage.getItem("authUserData1");
  const authUser = JSON.parse(authUserString);
  const loginUserID = authUser?.data?.[0]?.id;
  const CompanyID = authUser?.data?.[0]?.company_id;

  useEffect(() => {
    fetchOrders();
  }, [page]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `https://spillas.com/api/v1/keys/report?sender_id=${loginUserID}&mode=1&company_id=${CompanyID}&page=${page}&per_page=${rowperpage}`
      );

      const data = res.data;
      setOrders(data.data || []);
      setTotalPages(data.last_page || 1);
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  };

  // 🧾 Map API data to table rows
  const rows = orders.map((order, index) => [
    (page - 1) * rowperpage + index + 1, // Sr No.
    new Date(order.created_at).toLocaleDateString(), // Date
    `${order.receiver_name} (${order.receiver_code})`, // Retailer (Code)
    order.key_type === 1 ? "Regular" : "Smart", // Type
    order.package_name || "-", // Package
    order.qty, // Count
    `₹ ${order.key_rate}`,
    `₹ ${order.amount}`,
    Badge("Completed", "blue"), // Status
  ]);

  // 📦 Handle Download All Orders
  const handleDownload = async () => {
    try {
      setLoading(true);
      let allOrders = [];
      let currentPage = 1;
      const perPage = 200; // bigger batch to speed up

      while (true) {
        const res = await axios.get(
          `https://spillas.com/api/v1/keys/report?sender_id=${loginUserID}&mode=1&company_id=${CompanyID}&page=${currentPage}&per_page=${perPage}`
        );

        const pageData = res.data?.data || [];
        if (pageData.length === 0) break;

        allOrders = [...allOrders, ...pageData];
        const lastPage = res.data?.last_page || 1;
        if (currentPage >= lastPage) break;
        currentPage++;
      }

      if (allOrders.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "No Data",
          text: "No orders available to download.",
          confirmButtonColor: "#2563eb",
        });
        return;
      }

      // ✅ Define CSV headers
      const headers = [
        "Sr No.",
        "Date",
        "Retailer (Code)",
        "Type",
        "Package",
        "Count",
        "Key Rate",
        "Amount",
        "Status",
      ];

      // ✅ Build CSV rows
      const csvRows = [
        headers.join(","), // Header
        ...allOrders.map((order, i) => {
          return [
            i + 1,
            new Date(order.created_at).toLocaleDateString(),
            `${order.receiver_name} (${order.receiver_code})`,
            order.key_type === 1 ? "Regular" : "Smart",
            order.package_name || "-",
            order.qty,
            order.key_rate,
            order.amount,
            "Completed",
          ].join(",");
        }),
      ];

      // ✅ Convert to CSV file and download
      const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `orders_report_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      Swal.fire({
        icon: "success",
        title: "Download Ready",
        text: "Orders report downloaded successfully!",
        confirmButtonColor: "#2563eb",
      });
    } catch (error) {
      console.error("❌ Error downloading CSV:", error);
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "Unable to download orders report.",
        confirmButtonColor: "#2563eb",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex justify-between items-center">
        <Header brand={BRAND} title="Orders" />
      </div>
      <div className="flex justify-end gap-2">
        <BlueButton brand={BRAND} onClick={fetchOrders} className="inline-flex items-center gap-1">
          <RefreshCw size={14} /> Refresh
        </BlueButton>

        {/* 🧾 Download Button */}
        <BlueButton brand={BRAND}
          onClick={handleDownload}
          className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-all text-sm"
          disabled={loading}
        >
          <Download size={16} />
          {loading ? "Preparing..." : "Download"}
        </BlueButton>
      </div>

      {loading && orders.length === 0 ? (
        <p className="text-center text-gray-500">Loading orders...</p>
      ) : (
        <SimpleTable
          headers={[
            "Sr No.",
            "Date",
            "Retailer (Code)",
            "Type",
            "Package",
            "Count",
            "Key Rate",
            "Amount",
            "Status",
          ]}
          rows={rows}
        />
      )}

      {/* ✅ Pagination */}
      <div className="flex gap-2 justify-end mt-3 items-center text-sm">
        {/* Prev Button */}
        <button
          disabled={page === 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          className="px-2 py-1 border rounded disabled:opacity-50"
        >
          Prev
        </button>

        {/* Page Numbers */}
        {(() => {
          const visiblePages = [];
          const maxVisible = 5;
          let start = Math.max(1, page - 2);
          let end = Math.min(totalPages, start + maxVisible - 1);

          if (end - start < maxVisible - 1) {
            start = Math.max(1, end - maxVisible + 1);
          }

          if (start > 1) {
            visiblePages.push(
              <button
                key={1}
                onClick={() => setPage(1)}
                className="px-2 py-1 border rounded"
              >
                1
              </button>
            );
            if (start > 2) visiblePages.push(<span key="start-ellipsis">...</span>);
          }

          for (let i = start; i <= end; i++) {
            visiblePages.push(
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`px-2 py-1 border rounded ${
                  page === i ? "bg-blue-500 text-white border-blue-500" : ""
                }`}
              >
                {i}
              </button>
            );
          }

          if (end < totalPages) {
            if (end < totalPages - 1)
              visiblePages.push(<span key="end-ellipsis">...</span>);
            visiblePages.push(
              <button
                key={totalPages}
                onClick={() => setPage(totalPages)}
                className="px-2 py-1 border rounded"
              >
                {totalPages}
              </button>
            );
          }

          return visiblePages;
        })()}

        {/* Next Button */}
        <button
          disabled={page === totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          className="px-2 py-1 border rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}


// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import Header from "../Components/Header";
// import SimpleTable from "../Components/SimpleTable";
// import { BRAND } from "../css/brand";
// import Badge from "../Components/Badge";

// export default function Orders() {
//   const [orders, setOrders] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [page, setPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);
//   const rowperpage = 10

//   const authUserString = sessionStorage.getItem("authUserData1");
//   const authUser = JSON.parse(authUserString);
//   const loginUserID = authUser?.data?.[0]?.id;
//   const CompanyID = authUser?.data?.[0]?.company_id;

//   useEffect(() => {
//     fetchOrders();
//   }, [page]);

//   const fetchOrders = async () => {
//     setLoading(true);
//     try {
//       const res = await axios.get(
//         `https://spillas.com/api/v1/keys/report?sender_id=${loginUserID}&mode=1&company_id=${CompanyID}&page=${page}&per_page=${rowperpage}`
//       );

//       const data = res.data;
//       setOrders(data.data || []);
//       setTotalPages(data.last_page || 1);
//     } catch (err) {
//       console.error("Error fetching orders:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // 🧾 Map API data to table rows
//   const rows = orders.map((order, index) => [
//     (page - 1) * 10 + index + 1, // Sr No.
//     new Date(order.created_at).toLocaleDateString(), // Date
//     `${order.receiver_name} (${order.receiver_code})`, // Retailer (Code)
//     order.key_type === 1 ? "Regular" : "Smart", // Type
//     order.package_name || "-", // Package
//     order.qty, // Count
//     `₹ ${order.key_rate}`,
//     `₹ ${order.amount}`,
//     Badge("Completed", "blue") // Amount
//   ]);

//   return (
//     <div className="space-y-3 sm:space-y-4">
//       <Header brand={BRAND} title="Orders" />

//       {loading ? (
//         <p className="text-center text-gray-500">Loading orders...</p>
//       ) : (
//         <SimpleTable
//           headers={[
//             "Sr No.",
//             "Date",
//             "Retailer (Code)",
//             "Type",
//             "Package",
//             "Count",
//             "Key Rate",
//             "Amount",
//             "Status",
//           ]}
//           rows={rows}
//         />
//       )}

//       {/* ✅ Pagination */}
//       <div className="flex gap-2 justify-end mt-3 items-center text-sm">
//         {/* Prev Button */}
//         <button
//           disabled={page === 1}
//           onClick={() => setPage((p) => Math.max(1, p - 1))}
//           className="px-2 py-1 border rounded disabled:opacity-50"
//         >
//           Prev
//         </button>

//         {/* Page Numbers with Ellipsis */}
//         {(() => {
//           const visiblePages = [];
//           const maxVisible = 5; // number of pages to show
//           let start = Math.max(1, page - 2);
//           let end = Math.min(totalPages, start + maxVisible - 1);

//           if (end - start < maxVisible - 1) {
//             start = Math.max(1, end - maxVisible + 1);
//           }

//           // Show "1" and ellipsis if needed
//           if (start > 1) {
//             visiblePages.push(
//               <button
//                 key={1}
//                 onClick={() => setPage(1)}
//                 className="px-2 py-1 border rounded"
//               >
//                 1
//               </button>
//             );
//             if (start > 2) visiblePages.push(<span key="start-ellipsis">...</span>);
//           }

//           // Main page range
//           for (let i = start; i <= end; i++) {
//             visiblePages.push(
//               <button
//                 key={i}
//                 onClick={() => setPage(i)}
//                 className={`px-2 py-1 border rounded ${
//                   page === i ? "bg-blue-500 text-white border-blue-500" : ""
//                 }`}
//               >
//                 {i}
//               </button>
//             );
//           }

//           // Show ellipsis and last page if needed
//           if (end < totalPages) {
//             if (end < totalPages - 1)
//               visiblePages.push(<span key="end-ellipsis">...</span>);
//             visiblePages.push(
//               <button
//                 key={totalPages}
//                 onClick={() => setPage(totalPages)}
//                 className="px-2 py-1 border rounded"
//               >
//                 {totalPages}
//               </button>
//             );
//           }

//           return visiblePages;
//         })()}

//         {/* Next Button */}
//         <button
//           disabled={page === totalPages}
//           onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
//           className="px-2 py-1 border rounded disabled:opacity-50"
//         >
//           Next
//         </button>
//       </div>

//     </div>
//   );
// }



// import Header from "../components/Header";
// import SimpleTable from "../components/SimpleTable";
// import Badge from "../components/Badge";

// export default function Orders() {
//   return (
//     <div className="space-y-3 sm:space-y-4">
//       <Header title="Orders" />
//       <SimpleTable
//         headers={[ "Sr No.","Date","Retailer (Code)","Type","Package","Count","Amount", "Status"]}
//         rows={[["ORD-10013", "Maha Mobile Hub (RT-1001)", "₹ 52,400", "2025-09-22", Badge("Completed")], ["ORD-10014", "Sai Krupa (RT-1003)", "₹ 18,900", "2025-09-22", Badge("Pending", "red")]]}
//       />
//     </div>
//   );
// }